(() => {
  'use strict';

  if (window.AuraraHomeCookStory) {
    window.AuraraHomeCookStory.initAll();
    return;
  }

  const ARC = .75 * 2 * Math.PI * 92;
  const CIRCUMFERENCE = 2 * Math.PI * 92;

  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const phase = (progress, start, end) => clamp((progress - start) / (end - start));
  const colorToRgb = (value, fallback) => {
    const color = String(value || '').trim();
    const hex = color.match(/^#([\da-f]{3,8})$/i);

    if (hex) {
      const source = hex[1];
      const expanded = source.length === 3 || source.length === 4
        ? source.slice(0, 3).split('').map((part) => part + part).join('')
        : source.slice(0, 6);
      return [
        parseInt(expanded.slice(0, 2), 16),
        parseInt(expanded.slice(2, 4), 16),
        parseInt(expanded.slice(4, 6), 16),
      ];
    }

    const rgb = color.match(/[\d.]+/g);
    if (rgb && rgb.length >= 3) return rgb.slice(0, 3).map((part) => Number(part));

    return fallback;
  };

  const mixColor = (from, to, amount) => {
    const t = clamp(amount);
    return `rgb(${from.map((channel, index) => Math.round(channel + (to[index] - channel) * t)).join(', ')})`;
  };

  class AuraraHomeCookStoryController {
    constructor(root) {
      this.root = root;
      this.stage = root.querySelector('[data-cook-stage]');
      this.dial = root.querySelector('[data-cook-dial]');
      this.dialFill = root.querySelector('[data-dial-fill]');
      this.dialTemperature = root.querySelector('[data-dial-temperature]');
      this.steam = root.querySelector('[data-cook-steam]');
      this.cta = root.querySelector('[data-cook-cta]');
      this.scenes = {
        hands: root.querySelector('[data-cook-scene="hands"]'),
        top: root.querySelector('[data-cook-scene="top"]'),
        open: root.querySelector('[data-cook-scene="open"]'),
        bowl: root.querySelector('[data-cook-scene="bowl"]'),
      };
      this.captions = Array.from(root.querySelectorAll('[data-cook-caption]'));
      this.configureStepMedia();
      this.sceneTimeline = this.createSceneTimeline();
      this.flyCarrot = root.querySelector('.aurara-home-cook-story__flying-ingredient--carrot');
      this.flyChicken = root.querySelector('.aurara-home-cook-story__flying-ingredient--chicken');
      this.magneticButtons = Array.from(root.querySelectorAll('[data-cook-magnetic-button]'));
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.desktopQuery = window.matchMedia('(min-width: 990px)');
      this.animationsEnabled = root.dataset.cookScrollAnimations === 'true';
      this.pinEnabled = root.dataset.cookPinnedStory === 'true';
      const animationDistance = Number(root.dataset.cookAnimationScrollDistance ?? 200);
      const holdDistance = Number(root.dataset.cookEndingHoldDistance ?? 0);
      const animationScrollDistance = Number.isFinite(animationDistance) ? clamp(animationDistance, 100, 300) : 200;
      const endingHoldDistance = Number.isFinite(holdDistance) ? clamp(holdDistance, 0, 200) : 0;
      this.animationScrollShare = animationScrollDistance / (animationScrollDistance + endingHoldDistance);
      this.active = false;
      this.frame = null;
      this.lastFrameTime = 0;
      this.currentProgress = 0;
      this.magneticListeners = [];
      this.onScroll = this.requestUpdate.bind(this);
      this.onResize = this.requestUpdate.bind(this);
      this.onModeChange = this.refreshMode.bind(this);

      const computed = window.getComputedStyle(root);
      this.colors = {
        start: colorToRgb(computed.getPropertyValue('--aurara-cook-background-start'), [247, 243, 233]),
        middle: colorToRgb(computed.getPropertyValue('--aurara-cook-background-middle'), [239, 231, 214]),
        end: colorToRgb(computed.getPropertyValue('--aurara-cook-background-end'), [246, 229, 194]),
      };

      this.bindMagneticButtons();
      this.motionQuery.addEventListener('change', this.onModeChange);
      this.desktopQuery.addEventListener('change', this.onModeChange);
      this.refreshMode();
    }

    configureStepMedia() {
      this.originalStepMedia = [];
      const originals = new Map(Object.entries(this.scenes).map(([name, scene]) => [name, scene?.cloneNode(true)]));
      const stepScenes = ['hands', 'top', 'bowl'];
      const staticMedia = Array.from(this.root.querySelectorAll('.aurara-home-cook-story__static-step .aurara-home-cook-story__static-media'));
      this.captions.forEach((caption, index) => {
        const scene = this.scenes[stepScenes[index]];
        const template = this.root.querySelector(`[data-cook-step-media="${index}"]`);
        const selection = caption.dataset.cookStepScene;
        const selected = originals.get(selection);
        if (!scene || (!template && (!selected || selection === stepScenes[index]))) return;
        const content = template ? template.content : selected;
        [scene, staticMedia[index]].forEach((target) => {
          if (!target) return;
          this.originalStepMedia.push({ target, content: Array.from(target.childNodes, (node) => node.cloneNode(true)) });
          target.replaceChildren(...Array.from(content.childNodes, (node) => node.cloneNode(true)));
        });
      });
    }

    createSceneTimeline() {
      const readTemperature = (value) => {
        if (value === undefined || value.trim() === '') return null;
        const temperature = Number(value);
        return Number.isFinite(temperature) ? clamp(temperature, 20, 85) : null;
      };
      const temperatureToProgress = (temperature) => clamp((temperature - 20) / 65);
      const temperatures = [0, 1, 2].map((index) => readTemperature(this.captions[index]?.dataset.cookTriggerTemperature));
      const revealTemperature = readTemperature(this.root.dataset.cookRevealTemperature);
      const temperatureEndProgress = 1;
      const defaultScale = temperatureEndProgress / .96;
      const originalStarts = [0, .3, .82];
      const starts = temperatures.map((temperature, index) => temperature === null
        ? originalStarts[index] * defaultScale
        : temperatureToProgress(temperature));
      const revealComplete = revealTemperature === null
        ? .74 * defaultScale
        : temperatureToProgress(Math.max(20, revealTemperature - .5));
      const bowlStart = clamp(starts[2], revealComplete + .002, temperatureEndProgress - .002);
      const ending = [[bowlStart, .82], [temperatureEndProgress, .96]];
      if (revealComplete === 0) return [[0, .74], ...ending];
      const topComplete = Math.max(0, Math.min(temperatureToProgress(Math.max(20, (temperatures[1] ?? 35) - .5)), revealComplete - .002));
      if (topComplete === 0) return [[0, .44], [revealComplete, .74], ...ending];
      const handsStart = clamp(starts[0], 0, topComplete - .002);
      const topStart = handsStart + (topComplete - handsStart) * .3 / .44;
      return [[handsStart, 0], [topStart, .3], [topComplete, .44], [revealComplete, .74], ...ending];
    }

    getSceneProgress(progress) {
      if (!this.sceneTimeline) return progress;
      if (progress <= this.sceneTimeline[0][0]) return this.sceneTimeline[0][1];
      for (let index = 1; index < this.sceneTimeline.length; index += 1) {
        const [end, originalEnd] = this.sceneTimeline[index];
        if (progress <= end) {
          const [start, originalStart] = this.sceneTimeline[index - 1];
          return originalStart + phase(progress, start, end) * (originalEnd - originalStart);
        }
      }
      return 1;
    }

    get canAnimate() {
      return this.animationsEnabled && (!this.desktopQuery.matches || this.pinEnabled) && !this.motionQuery.matches;
    }

    refreshMode() {
      if (!this.canAnimate || !this.stage) {
        this.deactivate();
        return;
      }

      this.root.classList.remove('is-static');
      if (!this.active) {
        this.active = true;
        this.currentProgress = this.getTargetProgress();
        this.lastFrameTime = performance.now();
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onResize, { passive: true });
      }
      this.requestUpdate();
    }

    deactivate() {
      if (this.frame) window.cancelAnimationFrame(this.frame);
      this.frame = null;

      if (this.active) {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onResize);
      }

      this.active = false;
      this.root.classList.add('is-static');
      this.resetAnimatedStyles();
    }

    getTargetProgress() {
      if (!this.stage) return 0;
      const rootRect = this.root.getBoundingClientRect();
      const stageRect = this.stage.getBoundingClientRect();
      const travel = Math.max(1, (rootRect.height - stageRect.height) * this.animationScrollShare);
      return clamp((stageRect.top - rootRect.top) / travel);
    }

    requestUpdate() {
      if (!this.active || this.frame) return;
      this.frame = window.requestAnimationFrame((now) => this.update(now));
    }

    update(now) {
      this.frame = null;
      if (!this.active) return;

      const target = this.getTargetProgress();
      const elapsed = Math.min(.1, Math.max(.001, (now - this.lastFrameTime) / 1000));
      this.lastFrameTime = now;
      const catchUp = 1 - Math.pow(.01, elapsed / .6);

      this.currentProgress += (target - this.currentProgress) * catchUp;
      if (Math.abs(target - this.currentProgress) < .00025) this.currentProgress = target;

      this.render(this.currentProgress);
      if (Math.abs(target - this.currentProgress) >= .00025) this.requestUpdate();
    }

    render(progress) {
      const p = clamp(progress);
      const sceneProgress = this.getSceneProgress(p);
      this.renderBackground(p);

      const handsIn = phase(sceneProgress, 0, .18);
      const handsOut = phase(sceneProgress, .25, .35);
      this.setVisual(this.scenes.hands, handsIn * (1 - handsOut), -80 * (1 - handsIn), 0, 1 - .06 * handsOut);

      const carrotIn = phase(sceneProgress, .02, .24);
      const carrotOut = phase(sceneProgress, .25, .33);
      this.setFly(this.flyCarrot, carrotIn * (1 - carrotOut), -140 * (1 - carrotIn), 60 * (1 - carrotIn), -30 * (1 - carrotIn));

      const chickenIn = phase(sceneProgress, .05, .27);
      const chickenOut = phase(sceneProgress, .25, .33);
      this.setFly(this.flyChicken, chickenIn * (1 - chickenOut), 140 * (1 - chickenIn), 80 * (1 - chickenIn), 24 * (1 - chickenIn));

      const topIn = phase(sceneProgress, .3, .44);
      const topOut = phase(sceneProgress, .55, .65);
      this.setVisual(this.scenes.top, topIn * (1 - topOut), 0, -40 * topOut, .92 + .08 * topIn);

      const openIn = phase(sceneProgress, .58, .74);
      const openOut = phase(sceneProgress, .8, .9);
      this.setVisual(this.scenes.open, openIn * (1 - openOut), 0, 140 * (1 - openIn), 1 - .06 * openOut);

      const bowlIn = phase(sceneProgress, .82, .96);
      this.setVisual(this.scenes.bowl, bowlIn, 0, 60 * (1 - bowlIn), .9 + .1 * bowlIn);

      const steamIn = phase(sceneProgress, .62, .72);
      const steamOut = phase(sceneProgress, .72, .84);
      if (this.steam) {
        this.steam.style.opacity = String(.8 * steamIn * (1 - steamOut));
        this.steam.style.transform = `translateX(-50%) translateY(${24 * (1 - steamIn) - 30 * steamOut}px)`;
      }

      this.setCaption(0, phase(sceneProgress, .03, .18), phase(sceneProgress, .25, .35));
      this.setCaption(1, phase(sceneProgress, .32, .44), phase(sceneProgress, .8, .9));
      this.setCaption(2, phase(sceneProgress, .84, .96), 0);

      const temperatureProgress = p;
      this.setDial(temperatureProgress, false);

      if (this.dial) {
        const dialOut = phase(p, .86, 1);
        this.dial.style.opacity = '1';
        this.dial.style.transform = `scale(${1 - .45 * dialOut})`;
      }

      if (this.cta) {
        const ctaIn = phase(p, .9, 1);
        const ctaVisible = ctaIn >= .99;
        this.cta.style.opacity = String(ctaIn);
        this.cta.style.transform = `translateY(${20 * (1 - ctaIn)}px)`;
        this.cta.setAttribute('aria-hidden', String(!ctaVisible));
        this.setCtaFocusable(ctaVisible);
      }
    }

    renderBackground(progress) {
      if (!this.stage) return;
      let color;
      if (progress < .45) {
        color = mixColor(this.colors.start, this.colors.middle, progress / .45);
      } else if (progress < .6) {
        color = mixColor(this.colors.middle, this.colors.middle, 0);
      } else {
        color = mixColor(this.colors.middle, this.colors.end, phase(progress, .6, .9));
      }
      this.stage.style.setProperty('--aurara-cook-current-background', color);
    }

    setVisual(element, opacity, x, y, scale) {
      if (!element) return;
      const visibleOpacity = clamp(opacity);
      element.style.opacity = String(visibleOpacity);
      element.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${scale})`;
      element.setAttribute('aria-hidden', String(visibleOpacity < .01));
    }

    setFly(element, opacity, x, y, rotate) {
      if (!element) return;
      element.style.opacity = String(clamp(opacity));
      element.style.transform = `translate3d(${x}px, ${y}px, 0) rotate(${rotate}deg)`;
    }

    setCaption(index, entering, exiting) {
      const caption = this.captions[index];
      if (!caption) return;
      const visibleOpacity = clamp(entering * (1 - exiting));
      caption.style.opacity = String(visibleOpacity);
      caption.style.transform = `translateY(calc(-50% + ${40 * (1 - entering) - 30 * exiting}px))`;
      caption.setAttribute('aria-hidden', String(visibleOpacity < .01));
    }

    setDial(progress, staticState) {
      const fill = staticState ? 1 : clamp(progress);
      if (this.dialFill) this.dialFill.setAttribute('stroke-dasharray', `${ARC * fill} ${CIRCUMFERENCE}`);
      if (this.dialTemperature) this.dialTemperature.textContent = String(Math.round(20 + fill * 65));
    }

    setCtaFocusable(visible) {
      if (!this.cta) return;
      this.cta.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
        if (visible) {
          if (element.dataset.auraraOriginalTabindex) {
            element.setAttribute('tabindex', element.dataset.auraraOriginalTabindex);
            delete element.dataset.auraraOriginalTabindex;
          } else {
            element.removeAttribute('tabindex');
          }
        } else {
          if (element.hasAttribute('tabindex') && !element.dataset.auraraOriginalTabindex) {
            element.dataset.auraraOriginalTabindex = element.getAttribute('tabindex');
          }
          element.setAttribute('tabindex', '-1');
        }
      });
    }

    bindMagneticButtons() {
      this.magneticButtons.forEach((button) => {
        const onMove = (event) => {
          if (this.motionQuery.matches || (event.pointerType && event.pointerType !== 'mouse')) return;
          const bounds = button.getBoundingClientRect();
          const x = clamp((event.clientX - (bounds.left + bounds.width / 2)) * .12, -6, 6);
          const y = clamp((event.clientY - (bounds.top + bounds.height / 2)) * .18, -6, 6);
          button.style.setProperty('--aurara-magnetic-x', `${x}px`);
          button.style.setProperty('--aurara-magnetic-y', `${y}px`);
        };
        const onLeave = () => {
          button.style.setProperty('--aurara-magnetic-x', '0px');
          button.style.setProperty('--aurara-magnetic-y', '0px');
        };
        button.addEventListener('pointermove', onMove);
        button.addEventListener('pointerleave', onLeave);
        this.magneticListeners.push({ button, onMove, onLeave });
      });
    }

    resetAnimatedStyles() {
      if (this.stage) this.stage.style.removeProperty('--aurara-cook-current-background');
      Object.values(this.scenes).forEach((scene) => {
        if (!scene) return;
        scene.style.removeProperty('opacity');
        scene.style.removeProperty('transform');
        scene.removeAttribute('aria-hidden');
      });
      [this.flyCarrot, this.flyChicken, this.steam, this.cta, this.dial].forEach((element) => {
        if (!element) return;
        element.style.removeProperty('opacity');
        element.style.removeProperty('transform');
      });
      this.captions.forEach((caption) => {
        caption.style.removeProperty('opacity');
        caption.style.removeProperty('transform');
        caption.removeAttribute('aria-hidden');
      });
      if (this.cta) this.cta.removeAttribute('aria-hidden');
      this.setCtaFocusable(true);
      this.setDial(1, true);
    }

    destroy() {
      this.deactivate();
      this.originalStepMedia.forEach(({ target, content }) => target.replaceChildren(...content));
      this.motionQuery.removeEventListener('change', this.onModeChange);
      this.desktopQuery.removeEventListener('change', this.onModeChange);
      this.magneticListeners.forEach(({ button, onMove, onLeave }) => {
        button.removeEventListener('pointermove', onMove);
        button.removeEventListener('pointerleave', onLeave);
      });
      this.magneticListeners = [];
      this.root.classList.remove('is-static');
    }
  }

  const init = (scope = document) => {
    const roots = [];
    if (scope instanceof Element && scope.matches('[data-aurara-cook-story]')) roots.push(scope);
    if (scope.querySelectorAll) roots.push(...scope.querySelectorAll('[data-aurara-cook-story]'));

    roots.forEach((root) => {
      if (root.auraraHomeCookStoryController) root.auraraHomeCookStoryController.destroy();
      root.auraraHomeCookStoryController = new AuraraHomeCookStoryController(root);
    });
  };

  const destroy = (scope) => {
    if (!(scope instanceof Element)) return;
    const root = scope.matches('[data-aurara-cook-story]') ? scope : scope.querySelector('[data-aurara-cook-story]');
    if (root && root.auraraHomeCookStoryController) {
      root.auraraHomeCookStoryController.destroy();
      delete root.auraraHomeCookStoryController;
    }
  };

  window.AuraraHomeCookStory = { initAll: () => init(document), init, destroy };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
